/**
 * NewChatModal - Modal for creating new chats
 *
 * Allows selecting users and optionally creating a group with a name
 * Styled with Notion-like design
 */

import React, { useState } from 'react';
import { X, Users, Check, Loader2 } from 'lucide-react';
import { useTeamChatStore } from '../../lib/stores/teamChatStore';

interface NewChatModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function NewChatModal({ isOpen, onClose }: NewChatModalProps) {
  const { organizationUsers, createChat } = useTeamChatStore();

  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [isGroup, setIsGroup] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [creating, setCreating] = useState(false);

  if (!isOpen) return null;

  const handleUserToggle = (userId: string) => {
    setSelectedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleCreate = async () => {
    if (selectedUserIds.length === 0 || creating) return;

    setCreating(true);
    try {
      const chat = await createChat(
        selectedUserIds,
        isGroup ? groupName || 'Nuovo gruppo' : undefined
      );
      if (chat) {
        handleClose();
      }
    } catch (error) {
      console.error('Error creating chat:', error);
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    setSelectedUserIds([]);
    setIsGroup(false);
    setGroupName('');
    onClose();
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Auto-enable group mode if multiple users selected
  const effectiveIsGroup = isGroup || selectedUserIds.length > 1;

  return (
    <div className="fixed inset-0 z-[66] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
      />

      {/* Modal - Notion style */}
      <div
        className="relative bg-white w-full max-w-md overflow-hidden"
        style={{
          borderRadius: '8px',
          border: '1px solid rgba(55, 53, 47, 0.09)',
          boxShadow: 'rgba(55, 53, 47, 0.1) 0px 4px 12px, rgba(55, 53, 47, 0.04) 0px 16px 32px'
        }}
      >
        {/* Header - Notion style */}
        <div
          className="flex items-center justify-between px-4 py-3 bg-white"
          style={{ borderBottom: '1px solid rgba(55, 53, 47, 0.09)' }}
        >
          <h3 className="font-semibold" style={{ color: '#37352F' }}>Nuova chat</h3>
          <button
            onClick={handleClose}
            className="p-1.5 rounded transition-colors"
            style={{ color: '#787774' }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(55, 53, 47, 0.08)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        {/* Group toggle - Notion style */}
        <div
          className="px-4 py-3"
          style={{ borderBottom: '1px solid rgba(55, 53, 47, 0.06)' }}
        >
          <label className="flex items-center gap-3 cursor-pointer">
            <div
              className="w-9 h-5 rounded-full transition-colors relative"
              style={{ backgroundColor: effectiveIsGroup ? '#2383E2' : 'rgba(55, 53, 47, 0.16)' }}
              onClick={() => setIsGroup(!isGroup)}
            >
              <div
                className="absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform"
                style={{
                  left: effectiveIsGroup ? '18px' : '2px',
                  boxShadow: '0 1px 2px rgba(55, 53, 47, 0.15)'
                }}
              />
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4" style={{ color: '#787774' }} strokeWidth={1.5} />
              <span className="text-sm" style={{ color: '#37352F' }}>Crea gruppo</span>
            </div>
          </label>

          {/* Group name input - Notion style */}
          {effectiveIsGroup && (
            <input
              type="text"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="Nome del gruppo"
              className="mt-3 w-full px-3 py-2 text-sm outline-none transition-all"
              style={{
                color: '#37352F',
                borderRadius: '6px',
                border: '1px solid rgba(55, 53, 47, 0.16)'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#2383E2';
                e.target.style.boxShadow = '0 0 0 2px rgba(35, 131, 226, 0.2)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(55, 53, 47, 0.16)';
                e.target.style.boxShadow = 'none';
              }}
            />
          )}
        </div>

        {/* Users list - Notion style */}
        <div className="max-h-64 overflow-y-auto">
          {organizationUsers.length === 0 ? (
            <div className="text-center py-8 px-4" style={{ color: '#787774' }}>
              Nessun utente disponibile
            </div>
          ) : (
            organizationUsers.map(user => {
              const isSelected = selectedUserIds.includes(user.id);

              return (
                <button
                  key={user.id}
                  onClick={() => handleUserToggle(user.id)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 transition-colors text-left"
                  style={{
                    backgroundColor: isSelected ? 'rgba(35, 131, 226, 0.08)' : 'transparent',
                    borderBottom: '1px solid rgba(55, 53, 47, 0.04)'
                  }}
                  onMouseEnter={(e) => !isSelected && (e.currentTarget.style.backgroundColor = 'rgba(55, 53, 47, 0.04)')}
                  onMouseLeave={(e) => !isSelected && (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  {/* Avatar */}
                  {user.profile_image_url ? (
                    <img
                      src={user.profile_image_url}
                      alt={user.full_name}
                      className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0"
                      style={{ backgroundColor: 'rgba(55, 53, 47, 0.08)', color: '#37352F' }}
                    >
                      {getInitials(user.full_name)}
                    </div>
                  )}

                  {/* Name */}
                  <span className="flex-1 text-sm" style={{ color: '#37352F' }}>
                    {user.full_name}
                  </span>

                  {/* Checkbox - Notion style */}
                  <div
                    className="w-[18px] h-[18px] rounded flex items-center justify-center transition-colors flex-shrink-0"
                    style={{
                      backgroundColor: isSelected ? '#2383E2' : 'transparent',
                      border: isSelected ? 'none' : '2px solid rgba(55, 53, 47, 0.3)'
                    }}
                  >
                    {isSelected && <Check className="w-3 h-3 text-white" strokeWidth={2.5} />}
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Footer - Notion style */}
        <div
          className="px-4 py-3 flex items-center justify-between bg-white"
          style={{ borderTop: '1px solid rgba(55, 53, 47, 0.09)' }}
        >
          <span className="text-sm" style={{ color: '#787774' }}>
            {selectedUserIds.length} selezionati
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={handleClose}
              className="px-3 py-1.5 rounded-md transition-colors text-sm font-medium"
              style={{ color: '#37352F' }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(55, 53, 47, 0.08)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              Annulla
            </button>
            <button
              onClick={handleCreate}
              disabled={selectedUserIds.length === 0 || creating}
              className="px-3 py-1.5 rounded-md text-white text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#2383E2' }}
              onMouseEnter={(e) => !creating && selectedUserIds.length > 0 && (e.currentTarget.style.backgroundColor = '#1a6cb8')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#2383E2')}
            >
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creazione...
                </>
              ) : (
                'Crea chat'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
